/**
 * Brands Hooks
 *
 * React Query hooks for brand management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { BrandSource, CreateBrandRequest, UpdateBrandRequest } from '@/types/brands';
import {
  getBrands,
  getBrand,
  createBrand,
  updateBrand,
  deleteBrand,
  syncBrands,
  unlockAIDescription,
  generateAIDescription,
  uploadBrandMedia,
  deleteBrandMedia,
  reorderBrandMedia,
  getBrandAssets,
} from '@/lib/api/brands';

/**
 * Query key factory for brands.
 */
export const brandsKeys = {
  all: ['brands'] as const,
  lists: () => [...brandsKeys.all, 'list'] as const,
  list: (source?: BrandSource) => [...brandsKeys.lists(), { source }] as const,
  details: () => [...brandsKeys.all, 'detail'] as const,
  detail: (id: string) => [...brandsKeys.details(), id] as const,
};

/**
 * Get all brands for workspace.
 */
export function useBrands(source?: BrandSource) {
  return useQuery({
    queryKey: brandsKeys.list(source),
    queryFn: () => getBrands(source),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get single brand by ID.
 */
export function useBrand(brandId: string) {
  return useQuery({
    queryKey: brandsKeys.detail(brandId),
    queryFn: () => getBrand(brandId),
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get brand assets (logo slots + style image slots).
 */
export function useBrandAssets(brandId: string | undefined) {
  return useQuery({
    queryKey: brandId ? ([...brandsKeys.detail(brandId), 'assets'] as const) : ['brands', 'assets', 'empty'],
    queryFn: () => getBrandAssets(brandId!),
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create new brand.
 */
export function useCreateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBrandRequest) => createBrand(data),
    onSuccess: () => {
      // Invalidate all brand lists
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Update brand.
 */
export function useUpdateBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brandId, data }: { brandId: string; data: UpdateBrandRequest }) =>
      updateBrand(brandId, data),
    onSuccess: (_, variables) => {
      // Invalidate brand detail
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(variables.brandId) });
      // Invalidate all brand lists
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Delete brand.
 */
export function useDeleteBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandId: string) => deleteBrand(brandId),
    onSuccess: () => {
      // Invalidate all brand lists
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Sync brands from JTL.
 */
export function useSyncBrands() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncBrands(),
    onSuccess: () => {
      // Invalidate all brand queries after sync
      queryClient.invalidateQueries({ queryKey: brandsKeys.all });
    },
  });
}

/**
 * Unlock AI description for editing.
 */
export function useUnlockAIDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandId: string) => unlockAIDescription(brandId),
    onSuccess: (_, brandId) => {
      // Invalidate brand detail to refresh unlock status
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(brandId) });
    },
  });
}

/**
 * Generate AI description for brand using AI tool config.
 */
export function useGenerateAIDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      brandId,
      options,
    }: {
      brandId: string;
      options?: { system_prompt?: string; user_prompt?: string; model_id?: string };
    }) => generateAIDescription(brandId, options),
    onSuccess: (_, variables) => {
      // Invalidate brand detail to show new description
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(variables.brandId) });
      // Invalidate lists to update any displays
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Upload media to brand (max 5 images).
 */
export function useUploadBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brandId, file }: { brandId: string; file: File }) =>
      uploadBrandMedia(brandId, file),
    onSuccess: (_, variables) => {
      // Invalidate brand detail to show new media
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(variables.brandId) });
      // Invalidate lists (in case media count matters)
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Delete media from brand.
 */
export function useDeleteBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brandId, s3Key }: { brandId: string; s3Key: string }) =>
      deleteBrandMedia(brandId, s3Key),
    onSuccess: (_, variables) => {
      // Invalidate brand detail to show updated media
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(variables.brandId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: brandsKeys.lists() });
    },
  });
}

/**
 * Reorder brand media.
 */
export function useReorderBrandMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brandId, order }: { brandId: string; order: string[] }) =>
      reorderBrandMedia(brandId, order),
    onSuccess: (_, variables) => {
      // Invalidate brand detail to show new order
      queryClient.invalidateQueries({ queryKey: brandsKeys.detail(variables.brandId) });
    },
  });
}
